import {
  OrganizationBankAccountDTO,
  OrganizationBankAccountsApi,
  UpdateOrganizationBankAccountVerificationNameDTO,
} from '@reposit/api-client';
import { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import { FlashState } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/common.utils';
import { syncEntitiesAndGetResults } from '../entities/entities.sagas';
import { dismissFlashMessage, FlashMessagesActionTypes, setFlashMessage } from '../flash-messages/flash-messages.actions';
import SCHEMA from '../schema';
import { createOrganizationBankAccountsApi, runSagaWithAuth } from '../utils/api.utils';
import {
  FETCH_ORG_BANK_ACCOUNTS_STORE_KEY,
  fetchOrganizationBankAccountsFailed,
  fetchOrganizationBankAccountsRequested,
  fetchOrganizationBankAccountsSuccess,
  UPDATE_ORG_BANK_ACCOUNT_VERIFICATION_NAME_MODAL_STORE_KEY,
  UPDATE_ORG_BANK_ACCOUNT_VERIFICATION_NAME_STORE_KEY,
  updateOrganizationBankAccountVerificationNameFailed,
  updateOrganizationBankAccountVerificationNameRequested,
  updateOrganizationBankAccountVerificationNameSuccess,
} from './organization-bank-account.actions';
import { UpdateOrganizationBankAccountVerificationName } from './organization-bank-account.types';

// ****************
// WORKERS
// ****************

export function* getOrganizationBankAccounts({ payload }: { payload: { organizationId: string }; type: string }) {
  try {
    const organizationBankAccountsApi: OrganizationBankAccountsApi = yield createOrganizationBankAccountsApi();
    const apiResponse: AxiosResponse<OrganizationBankAccountDTO[]> = yield call(
      runSagaWithAuth(() => organizationBankAccountsApi.getBankAccounts(payload.organizationId)),
    );
    const ids: string[] = yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.organizationBankAccounts);
    yield put(fetchOrganizationBankAccountsSuccess(ids));
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(fetchOrganizationBankAccountsFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: FETCH_ORG_BANK_ACCOUNTS_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
}

export function* updateOrganizationBankAccountVerificationName({
  payload,
}: {
  payload: UpdateOrganizationBankAccountVerificationName;
  type: string;
}) {
  try {
    const { organizationId, id, verificationName } = payload;
    const organizationBankAccountsApi: OrganizationBankAccountsApi = yield createOrganizationBankAccountsApi();
    const requestPayload: UpdateOrganizationBankAccountVerificationNameDTO = { verificationName };
    const response: AxiosResponse<OrganizationBankAccountDTO> = yield call(
      runSagaWithAuth(() => organizationBankAccountsApi.updateVerificationName(organizationId, id, requestPayload)),
    );

    yield syncEntitiesAndGetResults(response.data, SCHEMA.organizationBankAccount);
    yield put(updateOrganizationBankAccountVerificationNameSuccess());
    yield put<FlashMessagesActionTypes>(dismissFlashMessage([UPDATE_ORG_BANK_ACCOUNT_VERIFICATION_NAME_MODAL_STORE_KEY]));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UPDATE_ORG_BANK_ACCOUNT_VERIFICATION_NAME_STORE_KEY,
        message: 'Success! Verification name updated!',
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(updateOrganizationBankAccountVerificationNameFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UPDATE_ORG_BANK_ACCOUNT_VERIFICATION_NAME_MODAL_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
}

// ****************
// WATCHERS
// ****************
export function* watchOrganizationUserSagas() {
  yield takeLatest(fetchOrganizationBankAccountsRequested.type, getOrganizationBankAccounts);
  yield takeLatest(updateOrganizationBankAccountVerificationNameRequested.type, updateOrganizationBankAccountVerificationName);
}
